import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { array, bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Helmet } from 'react-helmet-async';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { queryFeaturedProviders } from './LandingPage.duck';
import { propTypes } from '../../util/types';
import { getLocaleFromUrl } from '../../constants/locales';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { post } from '../../util/api';
import { convertNumberToMoney, priceData } from '../../util/currency';
import { ensureListing } from '../../util/data';
import { createURLParamsForListing } from '../../util/listingHelpers';
import { userLocation, locationBounds } from '../../util/maps';
import { findOptionsForSelectFilter } from '../../util/search';
import { DISCIPLINES_TO_SLIDE } from '../../components/DisciplineSlider/DisciplineSlider.helper';
import { buildLocalizedOutdoorRegions } from '../../config/outdoorRegions';
import {
  IconChevronUp,
  IconClose,
  IconQuestionAnswer,
  LayoutSingleColumn,
  LocationAutocompleteInput,
  NamedLink,
  Page,
  ResponsiveImage,
  ReviewRating,
  SectionExplore,
  SectionGiftCard,
  SectionHero,
  SectionListingsSkeleton,
  SectionLocal,
  SectionPartners,
} from '../../components';
import LandingAIChatWidget from '../../components/LandingAIChatWidget';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './LandingPage.module.css';
import classNames from 'classnames';

const WELCOME_CODE = 'WELCOME10';
const WELCOME_DISCOUNT_PENDING_KEY = 'crion-welcome-discount-pending';
const EUROPE_SEARCH_URL = '/s?bounds=52.54171205%2C22.87360127%2C35.86457571%2C-5.13899985';

const STORY_ENTRY_CARDS = [
  {
    key: 'stories',
    pathByLocale: {
      'en-US': '/outdoor-stories',
      'it-IT': '/storie-outdoor',
      'fr-FR': '/histoires-outdoor',
      'de-DE': '/outdoor-geschichten',
    },
    image: '/static/imgs/disciplines/hiking/mountainHiking.avif',
  },
  {
    key: 'activities',
    pathByLocale: {
      'en-US': '/outdoor-activities',
      'it-IT': '/attivita-outdoor',
      'fr-FR': '/activites-outdoor',
      'de-DE': '/outdoor-aktivitaeten',
    },
    image: '/static/imgs/disciplines/climbing/rockClimbing.avif',
  },
  {
    key: 'places',
    pathByLocale: {
      'en-US': '/outdoor-destinations',
      'it-IT': '/destinazioni-outdoor',
      'fr-FR': '/destinations-outdoor',
      'de-DE': '/outdoor-ziele',
    },
    image: '/static/imgs/disciplines/skiing/skiTouring.avif',
  },
];

const normalizeLocale = locale =>
  ['en-US', 'de-DE', 'fr-FR', 'it-IT'].includes(locale) ? locale : 'en-US';

const buildSignupSearch = () =>
  `?code=${encodeURIComponent(WELCOME_CODE)}&source=homepage&intent=customer`;

const markWelcomeDiscountPending = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(WELCOME_DISCOUNT_PENDING_KEY, WELCOME_CODE);
  } catch (e) {
    // Ignore storage errors; signup still carries the code in the URL.
  }
};

const clearWelcomeDiscountPending = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(WELCOME_DISCOUNT_PENDING_KEY);
  } catch (e) {
    // Ignore storage errors.
  }
};

const hasPendingWelcomeDiscount = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return false;
  try {
    return window.sessionStorage.getItem(WELCOME_DISCOUNT_PENDING_KEY) === WELCOME_CODE;
  } catch (e) {
    return false;
  }
};

const getStoryCards = (intl, locale) =>
  STORY_ENTRY_CARDS.map(card => ({
    ...card,
    eyebrow: intl.formatMessage({ id: `LandingPage.story.${card.key}.eyebrow`, defaultMessage: '' }),
    title: intl.formatMessage({ id: `LandingPage.story.${card.key}.title`, defaultMessage: '' }),
    description: intl.formatMessage({ id: `LandingPage.story.${card.key}.description`, defaultMessage: '' }),
    path: card.pathByLocale[locale] || card.pathByLocale['en-US'],
  }));

const buildLandingPageSchema = (config, intl, schemaImage, popularListings, explicitLocale) => {
  const siteTitle = config.marketplaceName;
  const marketplaceRootURL = config.marketplaceRootURL;
  const locale = explicitLocale || getLocaleFromUrl(); // e.g., "en-US"
  const language = locale.split('-')[0]; // e.g., "en"

  // Get translations for name and description
  const schemaTitle = intl.formatMessage({ id: 'Website.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({ id: 'Website.schemaDescription' }, { siteTitle });

  // Get translations for name and description
  const schemaTitleOutdoor = intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { siteTitle });
  const schemaDescriptionOutdoor = intl.formatMessage({ id: 'LandingPage.schemaDescription' }, { siteTitle });
  const homeLabel = intl.formatMessage({ id: "Breadcrumb.home" });

  const logoUrl = `${marketplaceRootURL}/static/logo/crion.png`;
  const currentUrl = `${marketplaceRootURL}/${locale}`;

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${currentUrl}/#website`,
      "url": currentUrl,
      "name": schemaTitle,
      "description": schemaDescription,
      "inLanguage": ["en", "it", "fr", "de"],
      "publisher": { "@id": "https://crion.org#organization" },
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${currentUrl}/s?pub_activity=has_any%3A{search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${currentUrl}/#webpage`,
      "url": currentUrl,
      "name": schemaTitleOutdoor,
      "description": schemaDescriptionOutdoor,
      "inLanguage": language,
      "isPartOf": {
        "@id": `${currentUrl}/#website`
      },
      "about": {
        "@id": `${marketplaceRootURL}#organization`
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": homeLabel,
            "item": currentUrl
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${marketplaceRootURL}#organization`,
      "name": siteTitle,
      "url": marketplaceRootURL,
      "logo": {
        "@type": "ImageObject",
        "url": logoUrl, //
        "width": 300,
        "height": 60
      },
      "sameAs": [
        "https://www.instagram.com/crion_mountains",
        "https://www.linkedin.com/company/crion",
        "https://www.facebook.com/crion.community/"
      ]
    }
  ];

  // Add ItemList for featured listings to help search engines index listing pages
  // through the homepage's authority and understand what content is featured.
  if (popularListings && popularListings.length > 0) {
    const validListings = popularListings
      .slice(0, 8)
      .filter(l => l?.id?.uuid && l?.attributes?.title);
    if (validListings.length > 0) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "@id": `${currentUrl}/#featuredListings`,
        "name": intl.formatMessage({
          id: 'LandingPage.schemaFeaturedListings',
          defaultMessage: 'Featured outdoor experiences on Crion',
        }),
        "url": currentUrl,
        "itemListElement": validListings.map((listing, index) => {
          const uuid = listing.id.uuid;
          const title = listing.attributes.title;
          // Build canonical listing URL using the new /{type}/{slug}/{uuid} format
          const urlParams = createURLParamsForListing(listing, locale, intl);
          const listingUrl = `${marketplaceRootURL}/${locale}/${urlParams.type}/${urlParams.slug}/${uuid}`;
          return {
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Product",
              "@id": listingUrl,
              "name": title,
              "url": listingUrl,
            },
          };
        }),
      });
    }
  }

  return schemas;
};

const SEO_ACTIVITY_BASE_PATHS = {
  'en-US': 'outdoor-activities',
  'de-DE': 'outdoor-aktivitaeten',
  'fr-FR': 'activites-outdoor',
  'it-IT': 'attivita-outdoor',
};

const DESTINATION_BASE_PATHS = {
  'en-US': 'outdoor-destinations',
  'de-DE': 'outdoor-ziele',
  'fr-FR': 'destinations-outdoor',
  'it-IT': 'destinazioni-outdoor',
};

// Mirror of placeTestData in SectionExplore — kept in sync manually.
// Only seoSlugs are needed here; images are irrelevant for the sr-only nav.
const EXPLORE_PLACES = [
  { key: 'CH', seoSlugs: { 'en-US': 'switzerland', 'de-DE': 'schweiz', 'fr-FR': 'suisse', 'it-IT': 'svizzera' } },
  { key: 'IT', seoSlugs: { 'en-US': 'italy', 'de-DE': 'italien', 'fr-FR': 'italie', 'it-IT': 'italia' } },
  { key: 'AU', seoSlugs: { 'en-US': 'austria', 'de-DE': 'osterreich', 'fr-FR': 'autriche', 'it-IT': 'austria' } },
  { key: 'FR', seoSlugs: { 'en-US': 'france', 'de-DE': 'frankreich', 'fr-FR': 'france', 'it-IT': 'francia' } },
  { key: 'DE', seoSlugs: { 'en-US': 'germany', 'de-DE': 'deutschland', 'fr-FR': 'allemagne', 'it-IT': 'germania' } },
  { key: 'ES', seoSlugs: { 'en-US': 'spain', 'de-DE': 'spanien', 'fr-FR': 'espagne', 'it-IT': 'spagna' } },
  { key: 'NO', seoSlugs: { 'en-US': 'norway', 'de-DE': 'norwegen', 'fr-FR': 'norvege', 'it-IT': 'norvegia' } },
  { key: 'FI', seoSlugs: { 'en-US': 'finland', 'de-DE': 'finnland', 'fr-FR': 'finlande', 'it-IT': 'finlandia' } },
];

const LANDING_LISTING_LIMIT = 8;
const PLANNER_STEP_COUNT = 6;
const PEOPLE_MIN = 1;
const PEOPLE_MAX = 12;

const LEVEL_OPTIONS = [
  {
    id: 'veryEasy',
    labelId: 'LandingPage.level.veryEasy.label',
    textId: 'LandingPage.level.veryEasy.text',
    promptId: 'LandingPage.level.veryEasy.prompt',
    fill: 0,
  },
  {
    id: 'easy',
    labelId: 'LandingPage.level.easy.label',
    textId: 'LandingPage.level.easy.text',
    promptId: 'LandingPage.level.easy.prompt',
    fill: 1,
  },
  {
    id: 'intermediate',
    labelId: 'LandingPage.level.intermediate.label',
    textId: 'LandingPage.level.intermediate.text',
    promptId: 'LandingPage.level.intermediate.prompt',
    fill: 2,
  },
  {
    id: 'difficult',
    labelId: 'LandingPage.level.difficult.label',
    textId: 'LandingPage.level.difficult.text',
    promptId: 'LandingPage.level.difficult.prompt',
    fill: 3,
  },
];

const DATE_OPTIONS = [
  {
    id: 'flexible',
    labelId: 'LandingPage.date.flexible.label',
    textId: 'LandingPage.date.flexible.text',
    promptId: 'LandingPage.date.flexible.prompt',
  },
  {
    id: 'weekend',
    labelId: 'LandingPage.date.weekend.label',
    textId: 'LandingPage.date.weekend.text',
    promptId: 'LandingPage.date.weekend.prompt',
  },
  {
    id: 'day',
    labelId: 'LandingPage.date.day.label',
    textId: 'LandingPage.date.day.text',
    promptId: 'LandingPage.date.day.prompt',
  },
  {
    id: 'week',
    labelId: 'LandingPage.date.week.label',
    textId: 'LandingPage.date.week.text',
    promptId: 'LandingPage.date.week.prompt',
  },
  {
    id: 'specific',
    labelId: 'LandingPage.date.specific.label',
    textId: 'LandingPage.date.specific.text',
    promptId: 'LandingPage.date.specific.prompt',
  },
];

const GROUP_OPTIONS = [
  {
    id: 'group',
    labelId: 'LandingPage.group.group.label',
    textId: 'LandingPage.group.group.text',
    groupType: 'group',
    promptId: 'LandingPage.group.group.prompt',
  },
  {
    id: 'private',
    labelId: 'LandingPage.group.private.label',
    textId: 'LandingPage.group.private.text',
    groupType: 'private',
    promptId: 'LandingPage.group.private.prompt',
  },
];

const BUDGET_OPTIONS = [
  {
    id: 'flexible',
    labelId: 'LandingPage.budget.flexible.label',
    textId: 'LandingPage.budget.flexible.text',
    min: null,
    max: null,
  },
  {
    id: 'low',
    labelId: 'LandingPage.budget.low.label',
    textId: 'LandingPage.budget.low.text',
    min: 0,
    max: 100,
  },
  {
    id: 'mid',
    labelId: 'LandingPage.budget.mid.label',
    textId: 'LandingPage.budget.mid.text',
    min: 100,
    max: 250,
  },
  {
    id: 'high',
    labelId: 'LandingPage.budget.high.label',
    textId: 'LandingPage.budget.high.text',
    min: 250,
    max: 500,
  },
  {
    id: 'custom',
    labelId: 'LandingPage.budget.custom.label',
    textId: 'LandingPage.budget.custom.text',
    min: null,
    max: null,
  },
];

const BUDGET_UNITS = [
  {
    id: 'person',
    labelId: 'LandingPage.budgetUnit.person.label',
    promptId: 'LandingPage.budgetUnit.person.prompt',
  },
  {
    id: 'session',
    labelId: 'LandingPage.budgetUnit.session.label',
    promptId: 'LandingPage.budgetUnit.session.prompt',
  },
];

const LOCATION_SHORTCUTS = [
  {
    id: 'flexible',
    labelId: 'LandingPage.location.flexible.label',
    promptId: 'LandingPage.location.flexible.prompt',
  },
  {
    id: 'nearby-alps',
    labelId: 'LandingPage.location.nearbyAlps.label',
    promptId: 'LandingPage.location.nearbyAlps.prompt',
  },
  {
    id: 'switzerland',
    labelId: 'LandingPage.location.switzerland.label',
    promptId: 'LandingPage.location.switzerland.prompt',
  },
  {
    id: 'dolomites',
    labelId: 'LandingPage.location.dolomites.label',
    promptId: 'LandingPage.location.dolomites.prompt',
  },
];

const PLANNER_DISCIPLINES = DISCIPLINES_TO_SLIDE;

const toReadableText = value =>
  String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const getLocalizedLabel = (intl, key) =>
  key ? intl.formatMessage({ id: `General.${key}`, defaultMessage: toReadableText(key) }) : '';

const t = (intl, id, defaultMessage, values) =>
  intl.formatMessage({ id, defaultMessage }, values);

const getOptionLabel = (intl, option, values) =>
  t(intl, option.labelId, option.defaultLabel || '', values);

const getOptionText = (intl, option) => t(intl, option.textId, option.defaultText || '');

const getOptionPrompt = (intl, option) => t(intl, option.promptId, option.defaultPrompt || '');

const getPlannerLocationLabel = locationValue => {
  const value = typeof locationValue === 'string' ? { search: locationValue } : locationValue || {};
  return (
    value?.selectedPlace?.address ||
    value?.selectedPlace?.place_name ||
    value?.search ||
    ''
  ).trim();
};

const trimText = (value, maxLength) =>
  String(value || '')
    .trim()
    .slice(0, maxLength);

const formatDateLabel = value => {
  if (!value) return null;
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
};

const getDatePreferencePrompt = (intl, planner) => {
  const dateOption = DATE_OPTIONS.find(option => option.id === planner.dateMode) || DATE_OPTIONS[0];
  if (planner.dateMode !== 'specific') {
    return getOptionPrompt(intl, dateOption);
  }
  const start = formatDateLabel(planner.startDate);
  const end = formatDateLabel(planner.endDate);
  return start && end
    ? t(intl, 'LandingPage.date.specific.promptWithDates', 'specific dates from {start} to {end}', { start, end })
    : t(intl, 'LandingPage.date.specific.promptIncomplete', 'specific dates, not selected yet');
};

const getDatePreferenceLabel = (intl, planner) => {
  const dateOption = DATE_OPTIONS.find(option => option.id === planner.dateMode) || DATE_OPTIONS[0];
  if (planner.dateMode !== 'specific') {
    return getOptionLabel(intl, dateOption);
  }
  const start = formatDateLabel(planner.startDate);
  const end = formatDateLabel(planner.endDate);
  return start && end ? `${start} - ${end}` : getOptionLabel(intl, dateOption);
};

const formatBudgetOptionLabel = (intl, option, currency) =>
  getOptionLabel(intl, option, { currency: currency || 'EUR' });

const getBudgetOption = planner =>
  BUDGET_OPTIONS.find(option => option.id === planner.budgetMode) || BUDGET_OPTIONS[0];

const getBudgetUnit = planner =>
  BUDGET_UNITS.find(option => option.id === planner.budgetUnit) || BUDGET_UNITS[0];

const parseBudgetAmount = value => {
  const number = Number.parseInt(String(value || '').trim(), 10);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

const getBudgetRange = planner => {
  const option = getBudgetOption(planner);
  if (option.id !== 'custom') {
    return { min: option.min, max: option.max };
  }
  const min = parseBudgetAmount(planner.budgetMin);
  const max = parseBudgetAmount(planner.budgetMax);
  return min != null && max != null && max < min ? { min: max, max: min } : { min, max };
};

const getBudgetRangeLabel = (intl, planner, currency) => {
  const option = getBudgetOption(planner);
  const { min, max } = getBudgetRange(planner);
  if (option.id === 'flexible') {
    return t(intl, 'LandingPage.budget.flexible.rangeLabel', 'Flexible budget');
  }
  if (min != null && max != null) {
    return min === 0
      ? t(intl, 'LandingPage.budget.upToRange', 'Up to {currency} {max}', { currency, max })
      : t(intl, 'LandingPage.budget.betweenRange', '{currency} {min} - {max}', { currency, min, max });
  }
  if (min != null) {
    return t(intl, 'LandingPage.budget.fromRange', 'From {currency} {min}', { currency, min });
  }
  if (max != null) {
    return t(intl, 'LandingPage.budget.upToRange', 'Up to {currency} {max}', { currency, max });
  }
  return t(intl, 'LandingPage.budget.custom.rangeLabel', 'Custom budget');
};

const getBudgetPreferenceLabel = (intl, planner, currency) => {
  const rangeLabel = getBudgetRangeLabel(intl, planner, currency);
  if (planner.budgetMode === 'flexible') return rangeLabel;
  return `${rangeLabel} ${getOptionPrompt(intl, getBudgetUnit(planner))}`;
};

const getBudgetPreferencePrompt = (intl, planner, currency) => {
  const option = getBudgetOption(planner);
  if (option.id === 'flexible') {
    return t(intl, 'LandingPage.budget.flexible.prompt', 'flexible budget, prioritize fit, quality, availability, and transparent value');
  }
  return `${getBudgetRangeLabel(intl, planner, currency)} ${getOptionPrompt(intl, getBudgetUnit(planner))}`;
};

const getBudgetSearchPrice = planner => {
  const { min, max } = getBudgetRange(planner);
  return min != null || max != null ? `${min || 0},${max || 2000}` : null;
};

const toPlainLatLng = value => {
  if (!value) return null;
  const lat = Number(value.lat);
  const lng = Number(value.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

const toPlainBounds = value => {
  const ne = toPlainLatLng(value?.ne);
  const sw = toPlainLatLng(value?.sw);
  return ne && sw ? { ne, sw } : null;
};

const getLocalizedPlannerRegionNames = region => {
  const names = region?.names || {};
  return {
    english: names.en || null,
    french: names.fr || null,
    german: names.de || null,
    italian: names.it || null,
  };
};

const getPlannerLocationPreferences = ({ intl, planner, selectedCountry, selectedCountryRegions }) => {
  const selectedPlace = planner.location?.selectedPlace || null;
  const selectedRegion = selectedCountryRegions.find(region => region.id === planner.locationRegion) || null;
  const countryName = selectedCountry?.name || '';
  const regionName = selectedRegion?.name || '';
  const placeLabel = getPlannerLocationLabel(planner.location);
  const multiLangAddressParts = selectedPlace?.multiLangAddressParts || {};

  if (planner.locationMode === 'specific') {
    return {
      mode: 'specific',
      rawText: placeLabel || planner.location?.search || '',
      label: placeLabel || planner.location?.search || '',
      city: selectedPlace?.city || multiLangAddressParts?.english?.city || null,
      region: multiLangAddressParts?.english?.region || null,
      country: multiLangAddressParts?.english?.country || null,
      localizedRegionNames: {
        english: multiLangAddressParts?.english?.region || null,
        french: multiLangAddressParts?.french?.region || null,
        german: multiLangAddressParts?.german?.region || null,
        italian: multiLangAddressParts?.italian?.region || null,
      },
      localizedCountryNames: {
        english: multiLangAddressParts?.english?.country || null,
        french: multiLangAddressParts?.french?.country || null,
        german: multiLangAddressParts?.german?.country || null,
        italian: multiLangAddressParts?.italian?.country || null,
      },
      origin: toPlainLatLng(selectedPlace?.origin),
      bounds: toPlainBounds(selectedPlace?.bounds),
    };
  }

  if (planner.locationMode === 'current') {
    const currentRawText = t(intl, 'LandingPage.location.currentRawText', 'My current location');
    const currentLabel = t(intl, 'LandingPage.location.nearMe.label', 'Near me');
    return {
      mode: 'nearMe',
      rawText: currentRawText,
      label: currentLabel,
      origin: toPlainLatLng(selectedPlace?.origin),
      bounds: toPlainBounds(selectedPlace?.bounds),
      radiusKm: 30,
    };
  }

  return {
    mode: 'flexible',
    rawText: regionName ? `${regionName}, ${countryName}` : countryName,
    label: regionName ? `${regionName}, ${countryName}` : countryName,
    country: selectedCountry?.names?.en || countryName || null,
    countryId: selectedCountry?.id || null,
    region: selectedRegion?.names?.en || regionName || null,
    regionId: selectedRegion?.id || null,
    localizedRegionNames: getLocalizedPlannerRegionNames(selectedRegion),
    localizedCountryNames: getLocalizedPlannerRegionNames(selectedCountry),
  };
};

const getPlannerPreferences = ({
  intl,
  planner,
  selectedCountry,
  selectedCountryRegions,
  selectedDisciplineLabel,
  selectedAbility,
  selectedGroup,
  currency,
}) => {
  const { min, max } = getBudgetRange(planner);
  return {
    source: 'landing',
    discipline: planner.discipline,
    disciplineLabel: selectedDisciplineLabel,
    ability: planner.ability,
    abilityLabel: selectedAbility ? getOptionLabel(intl, selectedAbility) : null,
    location: getPlannerLocationPreferences({ intl, planner, selectedCountry, selectedCountryRegions }),
    timing: {
      mode: planner.dateMode,
      label: getDatePreferenceLabel(intl, planner),
      dateFrom: planner.dateMode === 'specific' ? planner.startDate || null : null,
      dateTo: planner.dateMode === 'specific' ? planner.endDate || null : null,
    },
    budget: {
      mode: planner.budgetMode,
      unit: planner.budgetUnit,
      label: getBudgetPreferenceLabel(intl, planner, currency),
      currency,
      min,
      max,
      isSoftPreference: true,
    },
    group: {
      groupType: selectedGroup?.groupType || null,
      label: selectedGroup ? getOptionLabel(intl, selectedGroup) : null,
      people: planner.people,
    },
  };
};

const getAddressParts = (publicData, locale) => {
  const lang = normalizeLocale(locale).split('-')[0];
  const languageKey =
    lang === 'it' ? 'italian' : lang === 'fr' ? 'french' : lang === 'de' ? 'german' : 'english';
  return (
    publicData?.multiLangAddressParts?.[languageKey] ||
    publicData?.multiLangAddressParts?.english ||
    {}
  );
};

const getListingLocation = (publicData, locale) => {
  const addressParts = getAddressParts(publicData, locale);
  const place = addressParts.city || publicData?.city || addressParts.region || publicData?.region;
  const country = addressParts.country || publicData?.country;
  return [place, country].filter(Boolean).join(', ');
};

const getListingDuration = (intl, duration) => {
  if (!duration) return null;
  return intl.formatMessage({ id: 'ListingPage.days' }, { duration });
};

const getProviderInitials = (name, fallbackName) => {
  const parts = String(name || fallbackName)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return parts
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
};

const getImageVariantUrl = (image, preferredVariants = []) => {
  const variants = image?.attributes?.variants;
  if (!variants) return null;
  const variantName =
    preferredVariants.find(name => variants[name]?.url) ||
    Object.keys(variants).find(name => variants[name]?.url);
  return variantName ? variants[variantName].url : null;
};

const ProviderAvatar = props => {
  const { fallbackName, image, name } = props;
  const imageUrl = getImageVariantUrl(image, ['square-xsmall2x', 'square-xsmall', 'square-small']);
  return imageUrl ? (
    <img src={imageUrl} alt={name} className={css.experienceProviderImage} loading="lazy" />
  ) : (
    <span className={css.experienceProviderInitials}>{getProviderInitials(name, fallbackName)}</span>
  );
};

ProviderAvatar.defaultProps = { fallbackName: 'Crion guide', image: null };
ProviderAvatar.propTypes = { fallbackName: string, image: object, name: string.isRequired };

const SectionHeader = props => (
  <div className={css.sectionHeader}>
    <span className={css.eyebrow}>{props.eyebrow}</span>
    <h2 className={css.sectionTitle}>{props.title}</h2>
    {props.description ? <p className={css.sectionDescription}>{props.description}</p> : null}
  </div>
);

SectionHeader.defaultProps = { description: null };
SectionHeader.propTypes = { description: string, eyebrow: string.isRequired, title: string.isRequired };

const ExperiencePreviewCard = props => {
  const { listing, intl, locale } = props;
  const ensuredListing = ensureListing(listing);
  const { attributes = {}, images = [], author } = ensuredListing;
  const { title, price, publicData = {}, deleted } = attributes;
  const firstImage = images?.[0] || null;
  const disciplineLabel = getLocalizedLabel(intl, publicData.discipline || publicData.activity);
  const location = getListingLocation(publicData, intl.locale);
  const duration = getListingDuration(intl, publicData.duration);
  const groupLabel = publicData.groupType
    ? intl.formatMessage({
        id: publicData.groupType === 'group' ? 'General.sharedGroup' : 'General.privateGroup',
        defaultMessage: publicData.groupType === 'group' ? 'Shared group' : 'Private group',
      })
    : null;
  const guideName = author?.attributes?.profile?.displayName;
  const providerName = guideName || t(intl, 'LandingPage.listing.guideFallback', 'Crion guide');
  const providerImage = author?.profileImage || null;
  const { averageRating, ratingCount = 0 } = author?.attributes?.profile?.metadata || {};
  const priceMoney =
    price?.amount != null && price?.currency ? convertNumberToMoney(price.amount, price.currency) : price;
  const formattedPrice = priceMoney ? priceData(priceMoney, intl).formattedPrice : null;
  const priceText = formattedPrice
    ? intl.formatMessage({ id: 'General.priceFrom', defaultMessage: 'From {formattedPrice}' }, { formattedPrice })
    : null;
  const fallbackTitle = t(intl, 'LandingPage.listing.outdoorExperience', 'Outdoor experience');

  const cardContent = (
    <>
      <div className={css.experienceImageWrap}>
        <ResponsiveImage
          rootClassName={css.experienceImage}
          image={firstImage}
          alt={disciplineLabel || title || ''}
          variants={['landscape-crop', 'landscape-crop2x']}
        />
      </div>
      <div className={css.experienceBody}>
        <h3 className={css.experienceTitle}>{disciplineLabel || fallbackTitle}</h3>
        <div className={css.experienceLocation}>{location || fallbackTitle}</div>
        <div className={css.experienceFacts}>
          {duration ? <span>{duration}</span> : null}
          {groupLabel ? <span>{groupLabel}</span> : null}
        </div>
        {averageRating > 0 ? (
          <ReviewRating
            rootClassName={css.experienceRating}
            reviewStarClassName={css.experienceRatingIcon}
            rating={averageRating}
            ratingCount={ratingCount}
            isAccented
            showStars={false}
          />
        ) : null}
        <div className={css.experienceFooter}>
          <div className={css.experienceProvider}>
            <ProviderAvatar
              fallbackName={t(intl, 'LandingPage.listing.guideFallback', 'Crion guide')}
              image={providerImage}
              name={providerName}
            />
            <span className={css.experienceGuide}>{providerName}</span>
          </div>
          {priceText ? <span className={css.experiencePrice}>{priceText}</span> : null}
        </div>
      </div>
    </>
  );

  if (deleted) {
    return <article className={css.experienceCard}>{cardContent}</article>;
  }

  return (
    <NamedLink
      name="ListingPage"
      params={createURLParamsForListing(ensuredListing, locale || null, intl)}
      className={css.experienceCard}
      target="_blank"
    >
      {cardContent}
    </NamedLink>
  );
};

ExperiencePreviewCard.propTypes = {
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
};

const DEFAULT_LANDING_PLANNER = {
  discipline: PLANNER_DISCIPLINES[0]?.key || 'mountainHiking',
  ability: LEVEL_OPTIONS[1].id,
  location: { search: 'Switzerland', predictions: [], selectedPlace: { address: 'Switzerland' } },
  locationMode: 'flexible',
  locationCountry: 'switzerland',
  locationRegion: null,
  dateMode: 'flexible',
  startDate: '',
  endDate: '',
  budgetMode: 'flexible',
  budgetUnit: 'person',
  budgetMin: '',
  budgetMax: '',
  group: null,
  people: 2,
};

const LANDING_PLANNER_SESSION_KEY = 'crion-home-planner-session';

export const LandingPageComponent = props => {
  const {
    history,
    intl,
    location,
    scrollingDisabled,
    onManageDisableScrolling,
    popularListings,
    queryPopularListingsInProgress,
    featuredProviders,
    onQueryFeaturedProviders,
    onFetchCurrentUser,
    currentUser,
    interfaceLang,
  } = props;

  // Fetch real providers when Redux state is empty (e.g. hot-reload or direct visit)
  useEffect(() => {
    if (!featuredProviders || featuredProviders.length === 0) {
      onQueryFeaturedProviders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  // interfaceLang is set from the request URL in dataLoader.js (reliable on SSR).
  // intl.locale comes from IntlProvider which on SSR calls getLocaleFromUrl() without
  // a URL argument and falls back to the default locale — so it's NOT locale-aware on SSR.
  const locale = normalizeLocale(interfaceLang || intl.locale);
  const currency = config.currency || 'EUR';
  const isAuthenticated = !!currentUser?.id;

  const [planner, setPlanner] = useState(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return DEFAULT_LANDING_PLANNER;
    try {
      const raw = window.sessionStorage.getItem(LANDING_PLANNER_SESSION_KEY);
      return raw ? JSON.parse(raw)?.planner || DEFAULT_LANDING_PLANNER : DEFAULT_LANDING_PLANNER;
    } catch { return DEFAULT_LANDING_PLANNER; }
  });
  const [plannerStep, setPlannerStep] = useState(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return 0;
    try {
      const raw = window.sessionStorage.getItem(LANDING_PLANNER_SESSION_KEY);
      return raw ? JSON.parse(raw)?.plannerStep ?? 0 : 0;
    } catch { return 0; }
  });
  const shouldScrollToChatRef = useRef(false);
  const [aiPromptRequest, setAiPromptRequest] = useState(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    try {
      const authRaw = window.sessionStorage.getItem('crion-ai-planner-request');
      if (authRaw) {
        window.sessionStorage.removeItem('crion-ai-planner-request');
        shouldScrollToChatRef.current = true;
        return JSON.parse(authRaw);
      }
      const raw = window.sessionStorage.getItem(LANDING_PLANNER_SESSION_KEY);
      return raw ? JSON.parse(raw)?.aiPromptRequest || null : null;
    } catch { return null; }
  });
  const [locationStatus, setLocationStatus] = useState({ status: 'idle', message: '' });
  const [discountState, setDiscountState] = useState({ status: 'idle', message: '' });
  const [isMobileAiChatDocked, setIsMobileAiChatDocked] = useState(false);
  const [isMobileAiTrayDismissed, setIsMobileAiTrayDismissed] = useState(false);
  const [isDesktopAiChatFloating, setIsDesktopAiChatFloating] = useState(false);
  const [isDesktopAiChatMinimized, setIsDesktopAiChatMinimized] = useState(false);
  const [isDesktopAiChatDismissed, setIsDesktopAiChatDismissed] = useState(false);
  const aiPlannerSectionRef = useRef(null);
  const lastMobileDockPromptIdRef = useRef(null);

  const hasListings = Array.isArray(popularListings) && popularListings.length > 0;
  const storedDiscounts = currentUser?.attributes?.profile?.privateData?.storedDiscounts || [];
  const hasWelcomeDiscount = storedDiscounts.some(discount => discount.code === WELCOME_CODE && !discount.usedAt);
  const [visibleListingCount, setVisibleListingCount] = useState(LANDING_LISTING_LIMIT);
  const storyCards = useMemo(() => getStoryCards(intl, locale), [intl, locale]);
  const plannerDisciplines = useMemo(() => {
    const disciplineOptions = findOptionsForSelectFilter('discipline', config.search.defaultFilters)
      .filter(option => option.state !== 'archived')
      .map(option => option.key);
    const availableKeys = new Set(disciplineOptions);
    const visibleDisciplines = PLANNER_DISCIPLINES.filter(discipline => availableKeys.has(discipline.key));
    return visibleDisciplines.length > 0 ? visibleDisciplines : PLANNER_DISCIPLINES;
  }, [config.search.defaultFilters]);
  const plannerCountries = useMemo(() => buildLocalizedOutdoorRegions(locale), [locale]);
  const selectedCountry =
    plannerCountries.find(country => country.id === planner.locationCountry) || plannerCountries[0];
  const selectedCountryRegions = useMemo(() => selectedCountry?.regions || [], [selectedCountry]);

  const facebookImage = config.branding?.facebookImage;
  const twitterImage = config.branding?.twitterImage;
  const schemaImage = facebookImage
    ? facebookImage.startsWith('https://')
      ? facebookImage
      : `${config.marketplaceRootURL}${facebookImage}`
    : null;
  const twitterSchemaImage = twitterImage
    ? twitterImage.startsWith('https://')
      ? twitterImage
      : `${config.marketplaceRootURL}${twitterImage}`
    : null;

  const schema = buildLandingPageSchema(config, intl, schemaImage, popularListings, locale);

  const openSignup = useCallback(() => {
    markWelcomeDiscountPending();
    const signupPath = createResourceLocatorString('SignupPage', routeConfiguration, {}, {});
    history.push(`${signupPath}${buildSignupSearch()}`, {
      modal: true,
      from: `${location.pathname}${location.search || ''}${location.hash || ''}`,
      backgroundLocation: {
        pathname: location.pathname,
        search: location.search || '',
        hash: location.hash || '',
      },
    });
  }, [history, location, routeConfiguration]);

  const claimWelcomeDiscount = useCallback(() => {
    if (!isAuthenticated) { openSignup(); return; }
    setDiscountState({ status: 'loading', message: '' });
    post('/api/store-partner-discount', { voucherCode: WELCOME_CODE, source: 'homepage_signup' })
      .then(() => onFetchCurrentUser().catch(() => null))
      .then(() => {
        clearWelcomeDiscountPending();
        setDiscountState({
          status: 'success',
          message: t(intl, 'LandingPage.discount.saved', 'Your 10% discount is saved and will apply at checkout.'),
        });
      })
      .catch(e => {
        const errorCode = e?.error || e?.data?.error;
        const alreadySaved = errorCode === 'ALREADY_STORED';
        if (alreadySaved) {
          clearWelcomeDiscountPending();
        }
        setDiscountState({
          status: alreadySaved ? 'success' : 'error',
          message: alreadySaved
            ? t(intl, 'LandingPage.discount.alreadySaved', 'Your 10% discount is already saved.')
            : t(intl, 'LandingPage.discount.error', 'We could not save the discount right now. Please try again.'),
        });
      });
  }, [intl, isAuthenticated, onFetchCurrentUser, openSignup]);

  const closeDiscountToast = useCallback(() => {
    setDiscountState({ status: 'idle', message: '' });
  }, []);

  const goToNextPlannerStep = useCallback(() => {
    setPlannerStep(prev => Math.min(prev + 1, PLANNER_STEP_COUNT - 1));
  }, []);

  const goToPreviousPlannerStep = useCallback(() => {
    setPlannerStep(prev => Math.max(prev - 1, 0));
  }, []);

  const handleBudgetRangeChange = useCallback((field, value) => {
    const numericValue = value.replace(/[^\d]/g, '');
    setPlanner(prev => ({ ...prev, budgetMode: 'custom', [field]: numericValue }));
    setPlannerStep(4);
  }, []);

  const preventBudgetInputSubmit = useCallback(event => {
    if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); }
  }, []);

  const useCurrentPlannerLocation = useCallback(async () => {
    setLocationStatus({ status: 'loading', message: t(intl, 'LandingPage.location.findingCurrentArea', 'Finding your current area...') });
    try {
      const origin = await userLocation({ enableHighAccuracy: false, maximumAge: 3600000 });
      const currentLocationBoundsDistance = config.maps?.search?.currentLocationBoundsDistance || 30000;
      const bounds = locationBounds(origin, currentLocationBoundsDistance);
      setPlanner(prev => ({
        ...prev,
        locationMode: 'current',
        location: {
          search: t(intl, 'LandingPage.location.currentRawText', 'My current location'),
          predictions: [],
          selectedPlace: {
            address: t(intl, 'LandingPage.location.currentRawText', 'My current location'),
            origin,
            bounds,
          },
        },
        locationCountry: prev.locationCountry,
        locationRegion: null,
      }));
      setLocationStatus({ status: 'success', message: t(intl, 'LandingPage.location.currentSelected', 'Current area selected.') });
    } catch (e) {
      setLocationStatus({ status: 'error', message: t(intl, 'LandingPage.location.currentError', 'We could not access your location. You can type a city or keep it flexible.') });
    }
  }, [config.maps?.search?.currentLocationBoundsDistance, intl]);

  const runPlanner = useCallback(
    event => {
      event.preventDefault();
      if (!aiPromptRequest && plannerStep < PLANNER_STEP_COUNT - 1) {
        setPlannerStep(PLANNER_STEP_COUNT - 1);
        return;
      }
      const group = GROUP_OPTIONS.find(option => option.id === planner.group);
      if (!group) { setPlannerStep(PLANNER_STEP_COUNT - 1); return; }

      const shortcut = LOCATION_SHORTCUTS.find(option => option.id === planner.locationMode);
      const locationLabel = getPlannerLocationLabel(planner.location);
      const origin = planner.location?.selectedPlace?.origin;
      const locationPrompt =
        planner.locationMode === 'specific' && locationLabel
          ? `around ${locationLabel}`
          : planner.locationMode === 'current'
          ? origin?.lat && origin?.lng
            ? `near my current location, origin ${origin.lat},${origin.lng}`
            : 'near my current location'
          : planner.locationMode === 'flexible' && locationLabel
          ? `flexible within ${locationLabel}`
          : shortcut
          ? getOptionPrompt(intl, shortcut)
          : t(intl, 'LandingPage.location.flexible.prompt', 'where Crion has the strongest available matches');

      const disciplineLabel = getLocalizedLabel(intl, planner.discipline);
      const ability = LEVEL_OPTIONS.find(option => option.id === planner.ability) || LEVEL_OPTIONS[1];
      const datePrompt = getDatePreferencePrompt(intl, planner);
      const budgetPrompt = getBudgetPreferencePrompt(intl, planner, currency);
      const budgetSearchPrice = getBudgetSearchPrice(planner);
      const peopleLabel = t(
        intl,
        planner.people === 1 ? 'LandingPage.people.one' : 'LandingPage.people.other',
        planner.people === 1 ? '{count} person' : '{count} people',
        { count: planner.people }
      );
      const parts = [
        `discipline: ${disciplineLabel}`,
        `listing filter pub_discipline=${planner.discipline}`,
        `ability: ${getOptionPrompt(intl, ability)}`,
        `listing filter pub_ability=${planner.ability}`,
        locationPrompt,
        `timing: ${datePrompt}`,
        planner.dateMode === 'specific' && planner.startDate && planner.endDate
          ? `listing availability dates=${planner.startDate},${planner.endDate}`
          : null,
        `budget: ${budgetPrompt}`,
        budgetSearchPrice ? `listing filter price=${budgetSearchPrice}` : null,
        `group: ${getOptionPrompt(intl, group)} for ${peopleLabel}`,
        group.groupType ? `listing filter pub_groupType=${group.groupType}` : null,
        `listing filter pub_people=${planner.people}`,
      ].filter(Boolean);

      const intro = aiPromptRequest
        ? t(intl, 'LandingPage.aiPrompt.updateIntro', 'Update my Crion AI plan with these revised preferences')
        : t(intl, 'LandingPage.aiPrompt.newIntro', 'I am a first-time Crion visitor using the Crion AI planner');
      const instruction = aiPromptRequest
        ? t(intl, 'LandingPage.aiPrompt.updateInstruction', 'Compare against the earlier shortlist, refresh the recommendations, and explain what changed.')
        : t(intl, 'LandingPage.aiPrompt.newInstruction', 'Start with one short recommendation, then give me 3 strong bookable Crion matches when available.');
      const prompt = t(
        intl,
        'LandingPage.aiPrompt.template',
        '{intro}. Use this brief and do not ask the same setup questions again: {brief}. {instruction} Prioritize listings that match the provided listing filters, explain why each fits, and suggest one clear next action. If the brief is not enough, ask only one follow-up question.',
        { intro, brief: parts.join(', '), instruction }
      );
      const plannerPreferences = getPlannerPreferences({
        intl,
        planner,
        selectedCountry,
        selectedCountryRegions,
        selectedDisciplineLabel: disciplineLabel,
        selectedAbility: { ...ability, label: getOptionLabel(intl, ability) },
        selectedGroup: { ...group, label: getOptionLabel(intl, group) },
        currency,
      });
      setAiPromptRequest({
        id: `${Date.now()}-${prompt}`,
        prompt,
        displayMessage: aiPromptRequest
          ? t(intl, 'LandingPage.aiPrompt.updatedDisplayMessage', 'Updated preferences sent to Crion AI')
          : t(intl, 'LandingPage.aiPrompt.displayMessage', 'Preferences sent to Crion AI'),
        context: {
          location: trimText(plannerPreferences.location?.label || plannerPreferences.location?.rawText || '', 128),
          activities: plannerPreferences.discipline ? [plannerPreferences.discipline] : [],
          plannerPreferences,
        },
      });
    },
    [aiPromptRequest, currency, intl, planner, plannerStep, selectedCountry, selectedCountryRegions]
  );

  const promptExamples = useMemo(
    () => [
      {
        id: 'weekend',
        label: t(intl, 'LandingPage.promptExample.weekend.label', 'Weekend plan'),
        prompt: t(intl, 'LandingPage.promptExample.weekend.prompt', 'Find a beginner-friendly outdoor experience near Zurich for next weekend with small groups.'),
      },
      {
        id: 'private-guide',
        label: t(intl, 'LandingPage.promptExample.privateGuide.label', 'Private guide'),
        prompt: t(intl, 'LandingPage.promptExample.privateGuide.prompt', 'I want a private guide for two people in the Alps. Compare hiking, climbing, and via ferrata options.'),
      },
      {
        id: 'family',
        label: t(intl, 'LandingPage.promptExample.family.label', 'Family trip'),
        prompt: t(intl, 'LandingPage.promptExample.family.prompt', 'Plan an easy family-friendly mountain day with bookable Crion activities and practical tips.'),
      },
      {
        id: 'snow',
        label: t(intl, 'LandingPage.promptExample.snow.label', 'Snow ideas'),
        prompt: t(intl, 'LandingPage.promptExample.snow.prompt', 'What snow experiences on Crion fit an intermediate traveler this month?'),
      },
    ],
    [intl]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      window.sessionStorage.setItem(LANDING_PLANNER_SESSION_KEY, JSON.stringify({ planner, plannerStep, aiPromptRequest }));
    } catch {
      // Ignore storage failures.
    }
  }, [planner, plannerStep, aiPromptRequest]);

  useEffect(() => {
    if (!isMobileAiChatDocked || typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const viewport = window.visualViewport;
    const rootStyle = document.documentElement.style;

    const updateMobileSheetSize = () => {
      const height = viewport?.height || window.innerHeight || document.documentElement.clientHeight;
      if (height) {
        rootStyle.setProperty('--crion-ai-mobile-sheet-height', `${Math.round(height)}px`);
      }
    };

    updateMobileSheetSize();
    viewport?.addEventListener('resize', updateMobileSheetSize);
    viewport?.addEventListener('scroll', updateMobileSheetSize);
    window.addEventListener('resize', updateMobileSheetSize);

    return () => {
      viewport?.removeEventListener('resize', updateMobileSheetSize);
      viewport?.removeEventListener('scroll', updateMobileSheetSize);
      window.removeEventListener('resize', updateMobileSheetSize);
      rootStyle.removeProperty('--crion-ai-mobile-sheet-height');
    };
  }, [isMobileAiChatDocked]);

  useEffect(() => {
    if (!shouldScrollToChatRef.current) return;
    const el = typeof document !== 'undefined' ? document.getElementById('ai-planner') : null;
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (discountState.status !== 'error') return;
    const timeout = window.setTimeout(() => {
      setDiscountState(prev => (prev.status === 'error' ? { status: 'idle', message: '' } : prev));
    }, 8000);
    return () => window.clearTimeout(timeout);
  }, [discountState.status]);

  useEffect(() => {
    if (!isAuthenticated || !hasPendingWelcomeDiscount()) return;

    if (hasWelcomeDiscount) {
      clearWelcomeDiscountPending();
      return;
    }

    setDiscountState({ status: 'loading', message: '' });
    post('/api/store-partner-discount', { voucherCode: WELCOME_CODE, source: 'homepage_signup' })
      .then(() => onFetchCurrentUser().catch(() => null))
      .then(() => {
        clearWelcomeDiscountPending();
        setDiscountState({
          status: 'success',
          message: t(intl, 'LandingPage.discount.saved', 'Your 10% discount is saved and will apply at checkout.'),
        });
      })
      .catch(e => {
        const errorCode = e?.error || e?.data?.error;
        const alreadySaved = errorCode === 'ALREADY_STORED';
        if (alreadySaved) {
          clearWelcomeDiscountPending();
        }
        setDiscountState({
          status: alreadySaved ? 'success' : 'error',
          message: alreadySaved
            ? t(intl, 'LandingPage.discount.alreadySaved', 'Your 10% discount is already saved.')
            : t(intl, 'LandingPage.discount.error', 'We could not save the discount right now. Please try again.'),
        });
      });
  }, [hasWelcomeDiscount, intl, isAuthenticated, onFetchCurrentUser]);

  useEffect(() => {
    const promptId = aiPromptRequest?.id;
    if (!promptId || promptId === lastMobileDockPromptIdRef.current) return;
    lastMobileDockPromptIdRef.current = promptId;
    setIsDesktopAiChatDismissed(false);
    setIsDesktopAiChatMinimized(false);
    setIsDesktopAiChatFloating(false);
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches) {
      setIsMobileAiTrayDismissed(false);
      setIsMobileAiChatDocked(true);
    }
  }, [aiPromptRequest]);

  useEffect(() => {
    if (!aiPromptRequest || isDesktopAiChatDismissed || typeof window === 'undefined') {
      setIsDesktopAiChatFloating(false);
      return undefined;
    }

    let rafId = null;
    const updateFloatingState = () => {
      const sectionEl = aiPlannerSectionRef.current;
      const isMobile = window.matchMedia('(max-width: 760px)').matches;

      if (!sectionEl || isMobile) {
        setIsDesktopAiChatFloating(false);
        return;
      }

      if (isDesktopAiChatFloating || isDesktopAiChatMinimized) {
        return;
      }

      const rect = sectionEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const shouldFloat = rect.bottom < viewportHeight - 80;

      setIsDesktopAiChatFloating(shouldFloat);
    };

    const scheduleUpdate = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      rafId = window.requestAnimationFrame(updateFloatingState);
    };

    updateFloatingState();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [
    aiPromptRequest,
    isDesktopAiChatDismissed,
    isDesktopAiChatFloating,
    isDesktopAiChatMinimized,
  ]);

  const openMobileAiChat = useCallback(() => {
    setIsMobileAiTrayDismissed(false);
    setIsMobileAiChatDocked(true);
  }, []);

  const minimizeMobileAiChat = useCallback(() => {
    setIsMobileAiChatDocked(false);
  }, []);

  const closeMobileAiChat = useCallback(() => {
    setIsMobileAiChatDocked(false);
    setIsMobileAiTrayDismissed(true);
  }, []);

  const openDesktopAiChat = useCallback(() => {
    setIsDesktopAiChatDismissed(false);
    setIsDesktopAiChatFloating(true);
    setIsDesktopAiChatMinimized(false);
  }, []);

  const minimizeDesktopAiChat = useCallback(() => {
    setIsDesktopAiChatFloating(false);
    setIsDesktopAiChatMinimized(true);
    setIsDesktopAiChatDismissed(false);
  }, []);

  const closeDesktopAiChat = useCallback(() => {
    setIsDesktopAiChatFloating(false);
    setIsDesktopAiChatMinimized(false);
    setIsDesktopAiChatDismissed(true);
  }, []);

  const openAiChatOverlay = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches) {
      setIsMobileAiTrayDismissed(false);
      setIsMobileAiChatDocked(true);
      return;
    }

    setIsDesktopAiChatDismissed(false);
    setIsDesktopAiChatFloating(true);
    setIsDesktopAiChatMinimized(false);
  }, []);

  const activePlannerStep = Math.min(plannerStep, PLANNER_STEP_COUNT - 1);
  const selectedLocationShortcut = LOCATION_SHORTCUTS.find(option => option.id === planner.locationMode);
  const selectedLocationLabel =
    planner.locationMode === 'specific'
      ? getPlannerLocationLabel(planner.location) || t(intl, 'LandingPage.location.choosePlace', 'Choose a place')
      : planner.locationMode === 'current'
      ? t(intl, 'LandingPage.location.nearMe.label', 'Near me')
      : planner.locationMode === 'flexible' && getPlannerLocationLabel(planner.location)
      ? getPlannerLocationLabel(planner.location)
      : selectedLocationShortcut
      ? getOptionLabel(intl, selectedLocationShortcut)
      : t(intl, 'LandingPage.location.flexible.label', 'I am flexible');
  const selectedDisciplineLabel = getLocalizedLabel(intl, planner.discipline);
  const selectedAbility = LEVEL_OPTIONS.find(option => option.id === planner.ability) || LEVEL_OPTIONS[1];
  const selectedGroup = GROUP_OPTIONS.find(option => option.id === planner.group);
  const selectedDateLabel = getDatePreferenceLabel(intl, planner);
  const selectedBudgetLabel = getBudgetPreferenceLabel(intl, planner, currency);
  const selectedGroupLabel = selectedGroup
    ? t(intl, 'LandingPage.summary.groupValue', '{group}, {people}', {
        group: getOptionLabel(intl, selectedGroup),
        people: t(intl, planner.people === 1 ? 'LandingPage.people.one' : 'LandingPage.people.other', planner.people === 1 ? '{count} person' : '{count} people', { count: planner.people }),
      })
    : t(intl, 'LandingPage.summary.chooseGroup', 'Choose group, {people}', {
        people: t(intl, planner.people === 1 ? 'LandingPage.people.one' : 'LandingPage.people.other', planner.people === 1 ? '{count} person' : '{count} people', { count: planner.people }),
      });
  const canSubmitPlanner = !!planner.group;
  const preferenceSummaryItems = [
    { step: 0, label: t(intl, 'LandingPage.summary.activity', 'Activity'), value: selectedDisciplineLabel },
    { step: 1, label: t(intl, 'LandingPage.summary.level', 'Level'), value: getOptionLabel(intl, selectedAbility) },
    { step: 2, label: t(intl, 'LandingPage.summary.place', 'Place'), value: selectedLocationLabel },
    { step: 3, label: t(intl, 'LandingPage.summary.date', 'Date'), value: selectedDateLabel },
    { step: 4, label: t(intl, 'LandingPage.summary.budget', 'Budget'), value: selectedBudgetLabel },
    { step: 5, label: t(intl, 'LandingPage.summary.group', 'Group'), value: selectedGroupLabel },
  ];

  // Featured providers come from the dedicated cache endpoint (see duck/loadData).
  // No client-side memo needed — data arrives pre-sorted with profile images.

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      openGraphType="website"
      description={intl.formatMessage({ id: 'LandingPage.schemaDescription' }, { siteTitle: config.marketplaceName })}
      title={intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { siteTitle: config.marketplaceName })}
      facebookImages={schemaImage ? [{ url: schemaImage, width: 1200, height: 630 }] : []}
      twitterImages={twitterSchemaImage ? [{ url: twitterSchemaImage, width: 600, height: 314 }] : []}
      schema={schema}
      priority="high"
    >
      <Helmet>
        {schemaImage ? <link rel="preload" href={schemaImage} as="image" /> : null}
        <link rel="dns-prefetch" href="//api.crion.org" />
        <link rel="preconnect" href="//fonts.googleapis.com" />
      </Helmet>
      <LayoutSingleColumn topbar={<TopbarContainer currentPage="LandingPage" />} footer={<FooterContainer />}>
        <main className={css.page}>
          {/* Hidden navigation for SEO crawlers — mirrors the sr-only pattern used in
              SectionMoreListingsMaybe on ListingPage. SSR guarantees all links are in
              the initial HTML that Googlebot fetches before any JS runs. */}
          <nav
            aria-hidden="true"
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {/* Listing pages — loaded from listingsCache on SSR */}
            {Array.isArray(popularListings) && popularListings.length > 0 ? (
              <ul>
                {popularListings.map(listing => (
                  <li key={listing.id.uuid}>
                    <NamedLink name="ListingPage" params={createURLParamsForListing(listing, locale, intl)}>
                      {listing.attributes.title}
                    </NamedLink>
                  </li>
                ))}
              </ul>
            ) : null}
            {/* Discipline SEO pages — hero carousel */}
            <ul>
              {DISCIPLINES_TO_SLIDE.map(d => (
                <li key={d.key}>
                  <Link to={`/${locale}/${SEO_ACTIVITY_BASE_PATHS[locale] || 'outdoor-activities'}/${d.seoSlugs[locale] || d.seoSlugs['en-US']}`}>
                    {d.key}
                  </Link>
                </li>
              ))}
            </ul>
            {/* Country destination SEO pages — countries carousel */}
            <ul>
              {EXPLORE_PLACES.map(p => (
                <li key={p.key}>
                  <Link to={`/${locale}/${DESTINATION_BASE_PATHS[locale] || 'outdoor-destinations'}/${p.seoSlugs[locale] || p.seoSlugs['en-US']}`}>
                    {p.key}
                  </Link>
                </li>
              ))}
            </ul>
            {/* Blog / guide hub pages — story cards carousel */}
            <ul>
              {STORY_ENTRY_CARDS.map(card => (
                <li key={card.key}>
                  <Link to={`/${locale}${card.pathByLocale[locale] || card.pathByLocale['en-US']}`}>
                    {card.key}
                  </Link>
                </li>
              ))}
            </ul>
            {/* Provider profile pages — provider carousel (real providers only, not mocks) */}
            {Array.isArray(featuredProviders) && featuredProviders.length > 0 ? (
              <ul>
                {featuredProviders
                  .filter(p => p?.key && !p.key.startsWith('p'))
                  .map(p => (
                    <li key={p.key}>
                      <NamedLink name="ProfilePage" params={{ id: p.key }}>
                        {p.name || p.key}
                      </NamedLink>
                    </li>
                  ))}
              </ul>
            ) : null}
          </nav>
          {/* 1 — Hero + discount strip */}
          <section className={css.heroLegacyShell}>
            <SectionHero
              className={css.heroLegacy}
              history={history}
              location={location}
              onManageDisableScrolling={onManageDisableScrolling}
              titleText={t(intl, 'LandingPage.hero.title', 'Book outdoor experiences with trusted local guides.')}
              subtitleText={t(intl, 'LandingPage.hero.subtitle', 'Search guided hiking, skiing, climbing, biking, water sports, and more across Europe.')}
            />
            <div className={css.heroDiscountCta}>
              <span className={css.heroProofText}>
                {t(intl, 'LandingPage.hero.proof', 'Local experts · Secure booking · Experiences for every level')}
              </span>
              <button type="button" className={css.heroOfferLink} onClick={claimWelcomeDiscount}>
                {isAuthenticated
                  ? t(intl, 'LandingPage.hero.discountAuthenticated', 'Save your 10% first-booking discount')
                  : t(intl, 'LandingPage.hero.discountGuest', 'Create an account and get 10% off your first booking')}
              </button>
            </div>
          </section>

          {/* 2 — Listing inventory grid */}
          {discountState.message ? (
            <div
              className={classNames(css.discountToast, {
                [css.discountToastError]: discountState.status === 'error',
              })}
              role={discountState.status === 'error' ? 'alert' : 'status'}
              aria-live={discountState.status === 'error' ? 'assertive' : 'polite'}
            >
              <div className={css.discountToastIcon} aria-hidden="true">
                {discountState.status === 'error' ? '!' : '%'}
              </div>
              <div className={css.discountToastBody}>
                <p className={css.discountToastTitle}>
                  {discountState.status === 'error'
                    ? t(intl, 'LandingPage.discount.toastErrorTitle', 'Discount not saved')
                    : t(intl, 'LandingPage.discount.toastTitle', 'Discount saved')}
                </p>
                <p className={css.discountToastText}>{discountState.message}</p>
                {discountState.status === 'success' ? (
                  <NamedLink name="PromotionsPage" className={css.discountToastAction}>
                    {t(intl, 'LandingPage.discount.viewPromotions', 'View discounts')}
                  </NamedLink>
                ) : null}
              </div>
              <button
                type="button"
                className={css.discountToastClose}
                onClick={closeDiscountToast}
                aria-label={t(intl, 'LandingPage.discount.closeToast', 'Close discount message')}
              >
                <IconClose rootClassName={css.discountToastCloseIcon} />
              </button>
            </div>
          ) : null}

          <section className={css.inventorySection}>
            <div className={css.carouselHeaderRow}>
              <div>
                <span className={css.eyebrow}>{t(intl, 'LandingPage.inventory.eyebrow', 'Bookable experiences')}</span>
                <h2 className={css.sectionTitle}>{t(intl, 'LandingPage.inventory.title', 'See what you can book on Crion.')}</h2>
                <p className={css.sectionDescription}>{t(intl, 'LandingPage.inventory.description', 'Explore guided experiences for different activities, levels, and destinations.')}</p>
              </div>
            </div>
            {queryPopularListingsInProgress && !hasListings ? (
              <SectionListingsSkeleton className={css.sectionRoot} />
            ) : hasListings ? (
              <div className={css.listingGrid}>
                {popularListings.slice(0, visibleListingCount).map(listing => (
                  <ExperiencePreviewCard key={listing.id.uuid} listing={listing} intl={intl} locale={locale} />
                ))}
              </div>
            ) : null}
            {hasListings && visibleListingCount < popularListings.length ? (
              <div className={css.loadMoreRow}>
                <button
                  type="button"
                  className={css.primaryCta}
                  onClick={() => setVisibleListingCount(c => Math.min(c + LANDING_LISTING_LIMIT, popularListings.length))}
                >
                  {t(intl, 'LandingPage.actions.showMoreListings', 'Show more')}
                </button>
              </div>
            ) : null}
            <div className={css.sectionCta}>
              <Link to={EUROPE_SEARCH_URL} className={css.secondaryAction}>
                {t(intl, 'LandingPage.actions.browseAllActivities', 'Browse all activities')}
              </Link>
            </div>
          </section>

          {/* 3 — Inline AI planner */}
          <section
            id="ai-planner"
            ref={aiPlannerSectionRef}
            className={classNames(css.aiPlannerSection, { [css.aiPlannerSectionChatActive]: aiPromptRequest })}
          >
            <div className={css.plannerColumn}>
              {aiPromptRequest ? (
                <div className={css.preferenceSummary}>
                  <span className={css.preferenceSummaryTitle}>{t(intl, 'LandingPage.preferences.title', 'Preferences')}</span>
                  <div className={css.preferenceSummaryGrid}>
                    {preferenceSummaryItems.map(item => (
                      <button
                        key={item.label}
                        type="button"
                        className={classNames(css.preferenceSummaryItem, { [css.preferenceSummaryItemActive]: activePlannerStep === item.step })}
                        onClick={() => setPlannerStep(item.step)}
                      >
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              <form className={css.plannerForm} onSubmit={runPlanner}>
                <div className={css.plannerProgress}>
                  <span>{t(intl, 'LandingPage.planner.stepProgress', 'Step {step} of {total}', { step: activePlannerStep + 1, total: PLANNER_STEP_COUNT })}</span>
                  <div className={css.progressTrack} aria-hidden="true">
                    <span className={css.progressFill} style={{ width: `${((activePlannerStep + 1) / PLANNER_STEP_COUNT) * 100}%` }} />
                  </div>
                </div>

                <div className={css.plannerStep}>
                  {activePlannerStep === 0 ? (
                    <>
                      <span className={css.plannerStepKicker}>{t(intl, 'LandingPage.step.activity.kicker', 'Activity')}</span>
                      <h3>{t(intl, 'LandingPage.step.activity.title', 'What would you like to try?')}</h3>
                      <p>{t(intl, 'LandingPage.step.activity.description', 'Choose a discipline first. This maps directly to Crion listings.')}</p>
                      <div className={css.disciplineChoiceGrid}>
                        {plannerDisciplines.map(discipline => {
                          const label = getLocalizedLabel(intl, discipline.key);
                          const active = planner.discipline === discipline.key;
                          return (
                            <button
                              key={discipline.key}
                              type="button"
                              className={classNames(css.disciplineChoice, { [css.disciplineChoiceActive]: active })}
                              onClick={() => setPlanner(prev => ({ ...prev, discipline: discipline.key }))}
                              aria-pressed={active}
                            >
                              <img src={discipline.srcImg} alt="" className={css.disciplineChoiceImage} />
                              <span>{label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : null}

                  {activePlannerStep === 1 ? (
                    <>
                      <span className={css.plannerStepKicker}>{t(intl, 'LandingPage.step.level.kicker', 'Skill level')}</span>
                      <h3>{t(intl, 'LandingPage.step.level.title', 'What level should we match?')}</h3>
                      <p>{t(intl, 'LandingPage.step.level.description', 'This uses the same ability filter that appears on Crion search.')}</p>
                      <div className={css.levelChoiceGrid}>
                        {LEVEL_OPTIONS.map(level => (
                          <button
                            key={level.id}
                            type="button"
                            className={classNames(css.levelChoice, { [css.levelChoiceActive]: planner.ability === level.id })}
                            onClick={() => setPlanner(prev => ({ ...prev, ability: level.id }))}
                            aria-pressed={planner.ability === level.id}
                          >
                            <span className={css.levelSignal} aria-hidden="true">
                              {[0, 1, 2].map(index => (
                                <span key={index} className={classNames({ [css.levelSignalFilled]: index < level.fill })} />
                              ))}
                            </span>
                            <strong>{getOptionLabel(intl, level)}</strong>
                            <span>{getOptionText(intl, level)}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  ) : null}

                  {activePlannerStep === 2 ? (
                    <>
                      <span className={css.plannerStepKicker}>{t(intl, 'LandingPage.step.location.kicker', 'Location')}</span>
                      <h3>{t(intl, 'LandingPage.step.location.title', 'Where should we start looking?')}</h3>
                      <p>{t(intl, 'LandingPage.step.location.description', 'Stay flexible by country or region, use your current area, or type a specific place.')}</p>
                      <div className={css.locationModeTabs}>
                        <button
                          type="button"
                          className={classNames(css.locationModeButton, { [css.locationModeButtonActive]: planner.locationMode === 'flexible' })}
                          onClick={() => {
                            const country = selectedCountry || plannerCountries[0];
                            setPlanner(prev => ({
                              ...prev,
                              locationMode: 'flexible',
                              locationCountry: country?.id || prev.locationCountry,
                              locationRegion: null,
                              location: { search: country?.name || '', predictions: [], selectedPlace: country?.name ? { address: country.name } : null },
                            }));
                          }}
                        >
                          {t(intl, 'LandingPage.locationMode.flexible', 'Flexible area')}
                        </button>
                        <button
                          type="button"
                          className={classNames(css.locationModeButton, { [css.locationModeButtonActive]: planner.locationMode === 'specific' })}
                          onClick={() => setPlanner(prev => ({ ...prev, locationMode: 'specific', locationRegion: null }))}
                        >
                          {t(intl, 'LandingPage.locationMode.specific', 'Specific place')}
                        </button>
                        <button
                          type="button"
                          className={classNames(css.locationModeButton, { [css.locationModeButtonActive]: planner.locationMode === 'current' })}
                          onClick={useCurrentPlannerLocation}
                          disabled={locationStatus.status === 'loading'}
                        >
                          {locationStatus.status === 'loading'
                            ? t(intl, 'LandingPage.location.finding', 'Finding...')
                            : t(intl, 'LandingPage.location.nearMe.label', 'Near me')}
                        </button>
                      </div>

                      {planner.locationMode === 'specific' ? (
                        <LocationAutocompleteInput
                          rootClassName={css.aiLocationInputRoot}
                          inputClassName={css.aiLocationInput}
                          predictionsClassName={css.aiLocationPredictions}
                          iconClassName={css.aiLocationIcon}
                          placeholder={t(intl, 'LandingPage.location.specificPlaceholder', 'Try Zurich, Valais, Interlaken, Dolomites...')}
                          predictionScopes={['country', 'region', 'district', 'place', 'locality']}
                          useDefaultPredictions
                          closeOnBlur
                          autoFocus
                          input={{
                            id: 'LandingPageAIPlannerLocation',
                            name: 'LandingPageAIPlannerLocation',
                            value: planner.location,
                            onChange: value => setPlanner(prev => ({ ...prev, location: value, locationMode: 'specific' })),
                            onFocus: () => {},
                            onBlur: () => {},
                          }}
                          meta={{ touched: false, valid: true }}
                        />
                      ) : null}

                      {planner.locationMode === 'flexible' ? (
                        <>
                          <div className={css.countryChoiceGrid}>
                            {plannerCountries.map(country => {
                              const active = planner.locationCountry === country.id && !planner.locationRegion;
                              return (
                                <button
                                  key={country.id}
                                  type="button"
                                  className={classNames(css.countryChoice, { [css.countryChoiceActive]: active })}
                                  onClick={() =>
                                    setPlanner(prev => ({
                                      ...prev,
                                      locationMode: 'flexible',
                                      locationCountry: country.id,
                                      locationRegion: null,
                                      location: { search: country.name, predictions: [], selectedPlace: { address: country.name } },
                                    }))
                                  }
                                  aria-pressed={active}
                                >
                                  <img src={country.image} alt="" className={css.countryChoiceImage} />
                                  <span>{country.name}</span>
                                </button>
                              );
                            })}
                          </div>
                          {selectedCountryRegions.length ? (
                            <div className={css.regionChoiceBlock}>
                              <span>
                                {t(intl, 'LandingPage.location.regionHeading', 'Choose a region in {country}', {
                                  country: selectedCountry?.name,
                                })}
                              </span>
                              <div className={css.regionChoiceGrid}>
                                {selectedCountryRegions.map(region => {
                                  const active = planner.locationRegion === region.id;
                                  return (
                                    <button
                                      key={region.id}
                                      type="button"
                                      className={classNames(css.regionChoice, { [css.regionChoiceActive]: active })}
                                      onClick={() =>
                                        setPlanner(prev => ({
                                          ...prev,
                                          locationMode: 'flexible',
                                          locationCountry: selectedCountry.id,
                                          locationRegion: region.id,
                                          location: {
                                            search: region.searchQuery || region.name,
                                            predictions: [],
                                            selectedPlace: { address: region.name, place_name: region.searchQuery },
                                          },
                                        }))
                                      }
                                      aria-pressed={active}
                                    >
                                      {region.name}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null}
                        </>
                      ) : null}

                      {planner.locationMode === 'current' ? (
                        <div className={css.currentLocationCard}>
                          <strong>{t(intl, 'LandingPage.location.currentTitle', 'Using your current area')}</strong>
                          <span>{t(intl, 'LandingPage.location.currentText', 'Crion AI will start nearby, then suggest alternatives if there are better matches.')}</span>
                        </div>
                      ) : null}

                      {locationStatus.message ? (
                        <span className={classNames(css.locationStatus, { [css.locationStatusError]: locationStatus.status === 'error' })}>
                          {locationStatus.message}
                        </span>
                      ) : null}
                    </>
                  ) : null}

                  {activePlannerStep === 3 ? (
                    <>
                      <span className={css.plannerStepKicker}>{t(intl, 'LandingPage.step.timing.kicker', 'Timing')}</span>
                      <h3>{t(intl, 'LandingPage.step.timing.title', 'When could you go?')}</h3>
                      <p>{t(intl, 'LandingPage.step.timing.description', 'Start broad, like Google Flights flexible dates, or add exact dates if you know them.')}</p>
                      <div className={css.dateChoiceGrid}>
                        {DATE_OPTIONS.map(option => {
                          const active = planner.dateMode === option.id;
                          return (
                            <button
                              key={option.id}
                              type="button"
                              className={classNames(css.dateChoice, { [css.dateChoiceActive]: active })}
                              onClick={() => setPlanner(prev => ({ ...prev, dateMode: option.id }))}
                              aria-pressed={active}
                            >
                              <strong>{getOptionLabel(intl, option)}</strong>
                              <span>{getOptionText(intl, option)}</span>
                            </button>
                          );
                        })}
                      </div>
                      {planner.dateMode === 'specific' ? (
                        <div className={css.dateInputGrid}>
                          <label>
                            <span>{t(intl, 'LandingPage.date.startDate', 'Start date')}</span>
                            <input type="date" value={planner.startDate} onChange={event => setPlanner(prev => ({ ...prev, startDate: event.target.value }))} />
                          </label>
                          <label>
                            <span>{t(intl, 'LandingPage.date.endDate', 'End date')}</span>
                            <input type="date" value={planner.endDate} onChange={event => setPlanner(prev => ({ ...prev, endDate: event.target.value }))} />
                          </label>
                        </div>
                      ) : null}
                    </>
                  ) : null}

                  {activePlannerStep === 4 ? (
                    <>
                      <span className={css.plannerStepKicker}>{t(intl, 'LandingPage.step.budget.kicker', 'Budget')}</span>
                      <h3>{t(intl, 'LandingPage.step.budget.title', 'What budget feels right?')}</h3>
                      <p>{t(intl, 'LandingPage.step.budget.description', 'Keep it flexible if you are exploring. Choose a range if price should guide the first shortlist.')}</p>
                      <div className={css.budgetUnitTabs} aria-label={t(intl, 'LandingPage.budget.unitAriaLabel', 'Budget unit')}>
                        {BUDGET_UNITS.map(unit => {
                          const active = planner.budgetUnit === unit.id;
                          return (
                            <button
                              key={unit.id}
                              type="button"
                              className={classNames(css.budgetUnitButton, { [css.budgetUnitButtonActive]: active })}
                              onClick={() => setPlanner(prev => ({ ...prev, budgetUnit: unit.id }))}
                              aria-pressed={active}
                            >
                              {getOptionLabel(intl, unit)}
                            </button>
                          );
                        })}
                      </div>
                      <div className={css.budgetChoiceGrid}>
                        {BUDGET_OPTIONS.map(option => {
                          const active = planner.budgetMode === option.id;
                          return (
                            <button
                              key={option.id}
                              type="button"
                              className={classNames(css.budgetChoice, { [css.budgetChoiceActive]: active })}
                              onClick={() => setPlanner(prev => ({ ...prev, budgetMode: option.id }))}
                              aria-pressed={active}
                            >
                              <strong>{formatBudgetOptionLabel(intl, option, currency)}</strong>
                              <span>{getOptionText(intl, option)}</span>
                            </button>
                          );
                        })}
                      </div>
                      {planner.budgetMode === 'custom' ? (
                        <div className={css.budgetInputGrid}>
                          <label>
                            <span>{t(intl, 'LandingPage.budget.minLabel', 'Min {currency}', { currency })}</span>
                            <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="0" value={planner.budgetMin} onFocus={() => setPlannerStep(4)} onKeyDown={preventBudgetInputSubmit} onChange={event => handleBudgetRangeChange('budgetMin', event.target.value)} />
                          </label>
                          <label>
                            <span>{t(intl, 'LandingPage.budget.maxLabel', 'Max {currency}', { currency })}</span>
                            <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="500" value={planner.budgetMax} onFocus={() => setPlannerStep(4)} onKeyDown={preventBudgetInputSubmit} onChange={event => handleBudgetRangeChange('budgetMax', event.target.value)} />
                          </label>
                        </div>
                      ) : null}
                    </>
                  ) : null}

                  {activePlannerStep === 5 ? (
                    <>
                      <span className={css.plannerStepKicker}>{t(intl, 'LandingPage.step.group.kicker', 'Group')}</span>
                      <h3>{t(intl, 'LandingPage.step.group.title', 'Who is going?')}</h3>
                      <p>{t(intl, 'LandingPage.step.group.description', 'Choose the booking style and number of people. The AI will use this to avoid impossible matches.')}</p>
                      <div className={css.groupChoiceGrid}>
                        {GROUP_OPTIONS.map(group => (
                          <button
                            key={group.id}
                            type="button"
                            className={classNames(css.groupChoice, { [css.groupChoiceActive]: planner.group === group.id })}
                            onClick={() => setPlanner(prev => ({ ...prev, group: group.id }))}
                            aria-pressed={planner.group === group.id}
                          >
                            <strong>{getOptionLabel(intl, group)}</strong>
                            <span>{getOptionText(intl, group)}</span>
                          </button>
                        ))}
                      </div>
                      <div className={css.peopleSelector}>
                        <div>
                          <strong>{t(intl, 'LandingPage.people.title', 'Number of people')}</strong>
                          <span>{t(intl, 'LandingPage.people.description', 'For open groups this is seats needed. For private groups this is your party size.')}</span>
                        </div>
                        <div className={css.peopleStepper} aria-label={t(intl, 'LandingPage.people.title', 'Number of people')}>
                          <button
                            type="button"
                            className={css.peopleButton}
                            onClick={() => setPlanner(prev => ({ ...prev, people: Math.max(PEOPLE_MIN, prev.people - 1) }))}
                            disabled={planner.people <= PEOPLE_MIN}
                            aria-label={t(intl, 'LandingPage.people.decrease', 'Decrease number of people')}
                          >-</button>
                          <span className={css.peopleCount}>{planner.people}</span>
                          <button
                            type="button"
                            className={css.peopleButton}
                            onClick={() => setPlanner(prev => ({ ...prev, people: Math.min(PEOPLE_MAX, prev.people + 1) }))}
                            disabled={planner.people >= PEOPLE_MAX}
                            aria-label={t(intl, 'LandingPage.people.increase', 'Increase number of people')}
                          >+</button>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>

                <div className={css.plannerNav}>
                  {activePlannerStep > 0 ? (
                    <button type="button" className={css.plannerBackButton} onClick={goToPreviousPlannerStep}>
                      {t(intl, 'LandingPage.actions.back', 'Back')}
                    </button>
                  ) : <span />}
                  {aiPromptRequest ? (
                    <div className={css.plannerNavActions}>
                      {activePlannerStep < PLANNER_STEP_COUNT - 1 ? (
                        <button type="button" className={css.plannerBackButton} onClick={goToNextPlannerStep}>
                          {t(intl, 'LandingPage.actions.nextPreference', 'Next preference')}
                        </button>
                      ) : null}
                      <button type="submit" className={css.plannerSubmit} disabled={!canSubmitPlanner}>
                        <IconQuestionAnswer className={css.buttonIcon} />
                        <span>{t(intl, 'LandingPage.actions.updateAiPlan', 'Update AI plan')}</span>
                      </button>
                    </div>
                  ) : activePlannerStep < PLANNER_STEP_COUNT - 1 ? (
                    <button type="button" className={css.plannerSubmit} onClick={goToNextPlannerStep}>
                      {t(intl, 'LandingPage.actions.continue', 'Continue')}
                    </button>
                  ) : (
                    <button type="submit" className={css.plannerSubmit} disabled={!canSubmitPlanner}>
                      <IconQuestionAnswer className={css.buttonIcon} />
                      <span>{t(intl, 'LandingPage.actions.buildAiPlan', 'Build my AI plan')}</span>
                    </button>
                  )}
                </div>
              </form>
            </div>
            <div
              className={classNames(css.aiChatColumn, {
                [css.aiChatColumnDocked]: aiPromptRequest && isMobileAiChatDocked,
                [css.aiChatColumnFloating]:
                  aiPromptRequest && isDesktopAiChatFloating,
                [css.aiChatColumnFloatingMinimized]:
                  aiPromptRequest && isDesktopAiChatFloating && isDesktopAiChatMinimized,
              })}
            >
              {aiPromptRequest && isMobileAiChatDocked ? (
                <div className={css.mobileAiDockControls}>
                  <span>{t(intl, 'LandingPage.ai.mobileTrayTitle', 'Your Crion AI shortlist is ready')}</span>
                  <div>
                    <button
                      type="button"
                      className={css.mobileAiDockButton}
                      onClick={minimizeMobileAiChat}
                      aria-label={t(intl, 'LandingPage.ai.mobileTrayMinimize', 'Minimize AI chat')}
                    >
                      <IconChevronUp className={css.mobileAiDockMinimizeIcon} />
                    </button>
                    <button
                      type="button"
                      className={css.mobileAiDockButton}
                      onClick={closeMobileAiChat}
                      aria-label={t(intl, 'LandingPage.ai.mobileTrayClose', 'Close AI chat')}
                    >
                      <IconClose rootClassName={css.mobileAiDockCloseIcon} />
                    </button>
                  </div>
                </div>
              ) : null}
              {aiPromptRequest && isDesktopAiChatFloating && !isDesktopAiChatMinimized ? (
                <div className={css.desktopAiFloatingControls}>
                  <span>{t(intl, 'LandingPage.ai.mobileTrayTitle', 'Your Crion AI shortlist is ready')}</span>
                  <div>
                    <button
                      type="button"
                      className={css.desktopAiFloatingButton}
                      onClick={minimizeDesktopAiChat}
                      aria-label={t(intl, 'LandingPage.ai.mobileTrayMinimize', 'Minimize AI chat')}
                    >
                      <IconChevronUp className={css.desktopAiFloatingMinimizeIcon} />
                    </button>
                    <button
                      type="button"
                      className={css.desktopAiFloatingButton}
                      onClick={closeDesktopAiChat}
                      aria-label={t(intl, 'LandingPage.ai.mobileTrayClose', 'Close AI chat')}
                    >
                      <IconClose rootClassName={css.desktopAiFloatingCloseIcon} />
                    </button>
                  </div>
                </div>
              ) : null}
              <div className={css.aiSectionIntro}>
                <SectionHeader
                  eyebrow={t(intl, 'LandingPage.ai.eyebrow', 'Crion AI')}
                  title={t(intl, 'LandingPage.ai.title', 'Let Crion AI find a few good matches.')}
                  description={t(intl, 'LandingPage.ai.description', 'Answer six quick questions. We will suggest bookable experiences that fit your activity, level, destination, timing, and budget.')}
                />
              </div>
              {aiPromptRequest ? (
                <>
                  <div className={css.aiReadyPanel}>
                    <span className={css.aiReadyPulse} />
                    <div>
                      <strong>{t(intl, 'LandingPage.ai.readyTitle', 'Your shortlist is ready in the chat.')}</strong>
                      <p>{t(intl, 'LandingPage.ai.readyText', 'Keep chatting for details, or edit your preferences anytime to refresh the suggestions.')}</p>
                      <button
                        type="button"
                        className={css.aiPopoutButton}
                        onClick={openAiChatOverlay}
                      >
                        <IconQuestionAnswer className={css.aiPopoutIcon} />
                        <span>{t(intl, 'LandingPage.ai.popoutButton', 'Open floating chat')}</span>
                      </button>
                    </div>
                  </div>
                  <div className={css.aiDockChatBody}>
                    <LandingAIChatWidget
                      contextPage="landing"
                      defaultOpen
                      disableAutoScroll
                      history={history}
                      inlineMaxHeight={680}
                      locale={intl.locale}
                      promptRequest={aiPromptRequest}
                      resultsMode="explorer"
                      sessionPersistKey="crion-ai-shared-chat"
                      showTrigger={false}
                      suggestedPrompts={promptExamples}
                      signupGateButtonLabel={t(intl, 'LandingPage.ai.signupGateButton', 'Sign up and get 10% off')}
                      signupGateDescription={t(intl, 'LandingPage.ai.signupGateDescription', 'Create a free account to keep planning with Crion AI, save your discount, and return to your picks.')}
                      signupSearch={buildSignupSearch()}
                      variant="inline"
                    />
                  </div>
                </>
              ) : (
                <div className={css.aiWaitPanel}>
                  <div className={css.aiWaitHeader}>
                    <span className={css.aiWaitPulse} />
                    <span>{t(intl, 'LandingPage.ai.waiting', 'Crion AI is waiting')}</span>
                  </div>
                  <div className={css.aiWaitMessage}>
                    <strong>{t(intl, 'LandingPage.ai.waitTitle', 'Your first suggestions are a few taps away.')}</strong>
                    <p>{t(intl, 'LandingPage.ai.waitText', 'Complete the quick questions. When you submit, your shortlist will open in the chat.')}</p>
                  </div>
                  <div className={css.aiWaitSummary}>
                    {preferenceSummaryItems.map(item => (
                      <div key={item.label}>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* 4 — Trust / How it works */}
          {aiPromptRequest && !isMobileAiChatDocked && !isMobileAiTrayDismissed ? (
            <div className={css.mobileAiTray}>
              <button type="button" className={css.mobileAiTrayOpen} onClick={openMobileAiChat}>
                <IconQuestionAnswer className={css.mobileAiTrayIcon} />
                <span>
                  <strong>{t(intl, 'LandingPage.ai.mobileTrayTitle', 'Your Crion AI shortlist is ready')}</strong>
                  <small>{t(intl, 'LandingPage.ai.mobileTrayOpen', 'Open chat')}</small>
                </span>
              </button>
              <button
                type="button"
                className={css.mobileAiTrayClose}
                onClick={closeMobileAiChat}
                aria-label={t(intl, 'LandingPage.ai.mobileTrayClose', 'Close AI chat')}
              >
                <IconClose rootClassName={css.mobileAiTrayCloseIcon} />
              </button>
            </div>
          ) : null}
          {aiPromptRequest && isDesktopAiChatMinimized && !isDesktopAiChatFloating ? (
            <div className={css.desktopAiTray}>
              <button type="button" className={css.desktopAiTrayOpen} onClick={openDesktopAiChat}>
                <IconQuestionAnswer className={css.desktopAiTrayIcon} />
                <span>
                  <strong>{t(intl, 'LandingPage.ai.mobileTrayTitle', 'Your Crion AI shortlist is ready')}</strong>
                  <small>{t(intl, 'LandingPage.ai.mobileTrayOpen', 'Open chat')}</small>
                </span>
              </button>
              <button
                type="button"
                className={css.desktopAiTrayClose}
                onClick={closeDesktopAiChat}
                aria-label={t(intl, 'LandingPage.ai.mobileTrayClose', 'Close AI chat')}
              >
                <IconClose rootClassName={css.desktopAiTrayCloseIcon} />
              </button>
            </div>
          ) : null}

          {/* 5 - Destinations and local professionals */}
          <SectionExplore />
          <SectionLocal
            providers={featuredProviders && featuredProviders.length > 0 ? featuredProviders : null}
            isAuthenticated={isAuthenticated}
          />

          {/* 6 - Planning guides */}
          <section className={css.blogSection}>
            <div className={css.carouselHeaderRow}>
              <div>
                <span className={css.eyebrow}>{t(intl, 'LandingPage.blog.eyebrow', 'Planning guides')}</span>
                <h2 className={css.sectionTitle}>{t(intl, 'LandingPage.blog.title', 'Plan with a little local knowledge.')}</h2>
                <p className={css.sectionDescription}>{t(intl, 'LandingPage.blog.description', 'Explore guides to activities, destinations, and seasons before you book.')}</p>
              </div>
            </div>
            <div className={css.storyGrid}>
              {storyCards.map(card => (
                <Link key={card.path} to={`/${locale}${card.path}`} className={css.storyCard}>
                  <div className={css.storyImageWrap}>
                    <img src={card.image} alt="" className={css.storyImage} />
                    <span className={css.storyEyebrow}>{card.eyebrow}</span>
                  </div>
                  <div className={css.storyBody}>
                    <h3>{card.title}</h3>
                    <p>{card.description}</p>
                    <span className={css.readMore}>{t(intl, 'LandingPage.actions.readMore', 'Read more')}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* 7 - Final CTA */}
          <SectionGiftCard />

          <section className={css.finalCtaSection}>
            <div>
              <span className={css.eyebrow}>{t(intl, 'LandingPage.finalCta.eyebrow', 'First booking offer')}</span>
              <h2>{t(intl, 'LandingPage.finalCta.title', 'Save 10% on your first booking.')}</h2>
              <p>{t(intl, 'LandingPage.finalCta.description', 'Create a free account. Your discount is saved automatically and applied at checkout.')}</p>
            </div>
            <button type="button" className={css.primaryCta} onClick={claimWelcomeDiscount}>
              <span>
                {isAuthenticated
                  ? t(intl, 'LandingPage.finalCta.authenticatedButton', 'Save my discount')
                  : t(intl, 'LandingPage.finalCta.guestButton', 'Create account and save 10%')}
              </span>
            </button>
          </section>

          <SectionPartners />
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

LandingPageComponent.defaultProps = {
  currentUser: null,
  featuredProviders: [],
  interfaceLang: null,
  popularListings: [],
  queryPopularListingsInProgress: false,
};

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  featuredProviders: array,
  interfaceLang: string,
  popularListings: array,
  queryPopularListingsInProgress: bool,
  onManageDisableScrolling: func.isRequired,
  onQueryFeaturedProviders: func.isRequired,
  onFetchCurrentUser: func.isRequired,

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onQueryFeaturedProviders: () => dispatch(queryFeaturedProviders()),
  onFetchCurrentUser: () => dispatch(fetchCurrentUser()),
});

const mapStateToProps = state => {
  const { popularListings, queryPopularListingsInProgress, featuredProviders } = state.LandingPage;
  const { currentUser } = state.user;
  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    popularListings,
    queryPopularListingsInProgress,
    featuredProviders: featuredProviders || [],
    interfaceLang: state.ui.interfaceLang,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(LandingPageComponent);

export default LandingPage;
